import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { DependencyPanel } from "./DependencyPanel";
import { DocChip } from "../shipment/DocChip";
import { FilePreviewModal } from "../shipment/FilePreviewModal";
import { STATUS_META, PRIORITY_META, TASK_TRANSITIONS, type TaskStatus, type TaskRole } from "./taskMeta";
import "./TaskDetail.css";

interface Props {
  open: boolean;
  taskId: string | null;
  /** Planlama, atama, silme yetkisi (`manage:project`). */
  canManage: boolean;
  /** Durum / ilerleme / harcanan saat bildirme yetkisi — atanan kişi de olabilir. */
  canReport: boolean;
  onClose: () => void;
  onAddSubtask: (parentId: string) => void;
  onOpenTask: (id: string) => void;
}

const ROLES: TaskRole[] = ["owner", "manager", "auditor", "watcher"];

function fmtDateTime(iso: string | Date | null): string {
  return iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—";
}
/**
 * <input type="date"> için YEREL tarih — `toISOString()` KULLANMA.
 *
 * Kaydederken `new Date("2026-07-20T00:00:00")` yerel gece yarısını üretiyor
 * (UTC+3'te 2026-07-19T21:00Z); geri okurken `toISOString()` UTC gününü verdiği
 * için alan 19.07 gösteriyor, kart ise 20.07. Her kaydetmede tarih bir gün
 * geriye yürüyordu. Evin diğer drawer'ları da bu yerel biçimi kullanıyor.
 */
/** "YYYY-MM-DD" → yerel gece yarısının ISO karşılığı. `toDateInput` ile simetrik. */
export function localMidnight(ymd: string): string {
  return new Date(`${ymd}T00:00:00`).toISOString();
}

function toDateInput(iso: string | Date | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const p2 = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

/** Bir olayın `data`'sı — sunucu READ-zamanı isim/başlık çözer (bkz. task.events). */
interface EventData {
  from?: string | number; to?: string | number;
  start?: string | null; end?: string | null;
  role?: string; fileName?: string; parentId?: string | null;
  userName?: string | null; parentTitle?: string | null;
}
type FeedEvent = { type: string; actorName: string | null; createdAt: string | Date; data?: EventData | null };

/** Durum kodu → Türkçe etiket (enum sonradan değişirse kodun kendisi fallback). */
const statusLabel = (code: unknown): string =>
  (code != null && STATUS_META.task[code as TaskStatus]?.label) || String(code ?? "—");
const roleLabelOf = (code: unknown): string =>
  (code != null && STATUS_META.roleLabel[code as TaskRole]) || String(code ?? "—");
const fmtD = (d: string | null | undefined): string => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");

/**
 * Olay → insan-okur Türkçe cümle. Statik "durumu değiştirdi" yerine `data` ile
 * "durumu Yapılacak → Devam Ediyor yaptı". Her lookup fallback'li (sessiz boş satır yok).
 */
function eventText(ev: FeedEvent): string {
  const d = ev.data ?? {};
  switch (ev.type) {
    case "created": return "görevi oluşturdu";
    case "status_changed": return `durumu ${statusLabel(d.from)} → ${statusLabel(d.to)} yaptı`;
    case "completed": return `görevi tamamladı (${statusLabel(d.from)} → ${statusLabel(d.to)})`;
    case "progress_changed": return `ilerlemeyi %${Number(d.from)} → %${Number(d.to)} güncelledi`;
    case "dated": return `tarihleri ${fmtD(d.start)} – ${fmtD(d.end)} yaptı`;
    case "assigned": return `${d.userName ?? "—"} kişisini ${roleLabelOf(d.role)} olarak atadı`;
    case "unassigned": return `${d.userName ?? "—"} kişisinin ${roleLabelOf(d.role)} atamasını kaldırdı`;
    case "moved": return `görevi ${d.parentTitle ?? (d.parentId ? "(silinmiş görev)" : "kök")} altına taşıdı`;
    case "commented": return "yorum yaptı";
    case "attached": return `dosya ekledi: ${d.fileName ?? ""}`.trim();
    default: return ev.type;
  }
}

/** Olay ikonu — YALNIZ Icon.tsx'te gerçekten olan adlar. */
function eventIcon(ev: FeedEvent): string {
  switch (ev.type) {
    case "created": return "plus";
    case "assigned": case "unassigned": return "user";
    case "status_changed": return (ev.data?.to != null && STATUS_META.task[ev.data.to as TaskStatus]?.icon) || "refresh";
    case "completed": return "checkCircle";
    case "progress_changed": return "activity";
    case "dated": return "calendar";
    case "commented": return "edit";
    case "attached": return "fileText";
    case "moved": return "cornerDownRight";
    default: return "activity";
  }
}

/**
 * Sayısal metin alanı için "değişti mi?" kararı — DÜZ STRING KARŞILAŞTIRMASI YANLIŞ.
 *
 * Kullanıcı TR biçiminde "7,5" yazar, sunucu 7.5 döner ve `String(7.5)` "7.5" olur.
 * String karşılaştırması bu ikisini farklı sayar: Kaydet butonu kayıttan SONRA da
 * açık kalır ve arayüz "kaydedilmemiş değişiklik var" diye YANLIŞ bilgi verir
 * (kaydın kendisi başarılıdır — yalnız gösterge yalan söyler).
 *
 * Boş alan ↔ null EŞİT sayılır. Geçersiz metin ("abc") DEĞİŞMİŞ sayılır ki
 * kullanıcı Kaydet'e basıp doğrulama hatasını görebilsin.
 */
function numDirty(local: string, server: number | null | undefined): boolean {
  const s = server == null ? null : Number(server);
  if (local.trim() === "") return s !== null;
  const l = Number(local.replace(",", "."));
  if (!Number.isFinite(l)) return true;
  return s === null || l !== s;
}

export function TaskDetailDrawer({ open, taskId, canManage, canReport, onClose, onAddSubtask, onOpenTask }: Props) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<"detail" | "comments" | "activity">("detail");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const taskQ = trpc.project.task.get.useQuery({ id: taskId ?? "" }, { enabled: open && !!taskId, staleTime: 3_000 });
  const t = taskQ.data ?? null;
  const commentsQ = trpc.project.comment.list.useQuery({ taskId: taskId ?? "" }, { enabled: open && !!taskId && tab === "comments", staleTime: 3_000 });
  const eventsQ = trpc.project.task.events.useQuery({ taskId: taskId ?? "" }, { enabled: open && !!taskId && tab === "activity", staleTime: 5_000 });
  const usersQ = trpc.project.project.assignableUsers.useQuery(undefined, { enabled: open, staleTime: 5 * 60_000 });

  const invalidate = () => qc.invalidateQueries();
  const fail = (e: { message: string }) => setErr(e.message);

  const updateMut = trpc.project.task.update.useMutation({ onSuccess: invalidate, onError: fail });
  const statusMut = trpc.project.task.setStatus.useMutation({ onSuccess: invalidate, onError: fail });
  const assignMut = trpc.project.task.setAssignee.useMutation({ onSuccess: invalidate, onError: fail });
  const commentMut = trpc.project.comment.create.useMutation({ onSuccess: invalidate, onError: fail });
  const commentEditMut = trpc.project.comment.update.useMutation({ onSuccess: invalidate, onError: fail });
  const commentDelMut = trpc.project.comment.remove.useMutation({ onSuccess: invalidate, onError: fail });
  const uploadMut = trpc.project.attachment.upload.useMutation({ onSuccess: invalidate, onError: fail });
  const attDelMut = trpc.project.attachment.remove.useMutation({ onSuccess: invalidate, onError: fail });

  // Düzenlenebilir alanlar — kaydedilene kadar yerelde tutulur.
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("medium");
  const [progress, setProgress] = useState(0);
  const [pStart, setPStart] = useState("");
  const [pEnd, setPEnd] = useState("");
  const [estimate, setEstimate] = useState("");
  const [spent, setSpent] = useState("");
  const [assignRole, setAssignRole] = useState<TaskRole>("owner");

  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState<{ id: string; author: string; quote: boolean } | null>(null);
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const [delComment, setDelComment] = useState<{ id: string; who: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Görev değişince / drawer kapanınca TÜM yerel durumu sıfırla.
  // `seededFor` = form alanlarının hangi görev için doldurulduğu. Sıfırlanınca
  // aşağıdaki efekt yeniden doldurur.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    setErr(null); setTab("detail"); setBody(""); setReplyTo(null); setEditing(null);
    seededFor.current = null; // aynı görev tekrar açılsa bile alanlar TAZE gelsin
  }, [open, taskId]);

  // Sunucudan gelen SON değerler — "kullanıcı bu alanı elle değiştirdi mi?" kararı
  // bunlarla verilir.
  const serverSnap = useRef<Record<string, string | number>>({});

  /**
   * Alan eşitleme kuralı: bir alanı YALNIZCA kullanıcı ona dokunmamışsa (yerel
   * değer, en son gösterilen sunucu değerine eşitse) sunucudaki yeni değere çek.
   *
   * İki uç arasında doğru nokta burası:
   *  · Her `t` değişiminde koşulsuz doldurmak, kullanıcının YAZDIĞI ama
   *    kaydetmediği metni siliyordu (atama eklemek/dosya yüklemek yazıyı uçuruyordu).
   *  · Yalnız bir kez doldurmak ise TERS hataya yol açtı: drawer içinden yapılan
   *    sunucu değişiklikleri (durum butonu %100 yazıyor, alt görev eklemek üst
   *    görevin yüzdesini türetiyor) yerel state'e hiç yansımıyor, `dirty` sürekli
   *    açık kalıyor ve Kaydet BAYAT değeri geri yazıyordu.
   */
  useEffect(() => {
    if (!t) return;
    const fresh = seededFor.current !== t.id;
    seededFor.current = t.id;
    const snap = serverSnap.current;
    const sync = <T,>(key: string, next: T, local: T, setter: (v: T) => void) => {
      if (fresh || local === (snap[key] as unknown as T)) setter(next);
      (snap as Record<string, unknown>)[key] = next;
    };
    sync("title", t.title, title, setTitle);
    sync("desc", t.description ?? "", desc, setDesc);
    sync("priority", t.priority, priority, setPriority);
    sync("progress", t.progressPct, progress, setProgress);
    sync("pStart", toDateInput(t.plannedStart), pStart, setPStart);
    sync("pEnd", toDateInput(t.plannedEnd), pEnd, setPEnd);
    sync("estimate", t.estimateHours != null ? String(t.estimateHours) : "", estimate, setEstimate);
    sync("spent", t.spentHours != null ? String(t.spentHours) : "", spent, setSpent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Alt görevi olan görevde ilerleme ÇOCUKLARDAN türetilir — sunucu elle girişi reddeder,
  // o yüzden alanı burada da kilitliyoruz (kullanıcı kesin reddedilecek bir değeri yazmasın).
  const hasChildren = (t?.childCount ?? 0) > 0;
  const dirty = useMemo(() => {
    if (!t) return false;
    // TRIM'Lİ KARŞILAŞTIR: save() `title.trim()` gönderiyor, sunucu da zod ile
    // trim'liyor. Karşılaştırmayı ham metinle yapmak, sonu boşluklu bir başlıkta
    // `dirty`yi sonsuza dek açık bırakıyordu (numDirty'deki hatanın metin hâli).
    return title.trim() !== t.title || desc.trim() !== (t.description ?? "") || priority !== t.priority
      || progress !== t.progressPct || pStart !== toDateInput(t.plannedStart) || pEnd !== toDateInput(t.plannedEnd)
      || numDirty(estimate, t.estimateHours) || numDirty(spent, t.spentHours);
  }, [t, title, desc, priority, progress, pStart, pEnd, estimate, spent]);

  async function save() {
    if (!t) return;
    setErr(null);
    const payload: Record<string, unknown> = { id: t.id };
    if (title.trim() !== t.title) payload.title = title.trim();
    if (desc.trim() !== (t.description ?? "")) payload.description = desc.trim() || null;
    if (priority !== t.priority) payload.priority = priority;
    if (progress !== t.progressPct) payload.progressPct = progress;
    // YEREL gece yarısı — `toDateInput` de yerel okuyor. Form drawer'ları da aynı
    // sözleşmeyi kullanır; karışık kullanılınca (biri UTC biri yerel) aynı takvim
    // günü için pe < ps çıkıyor ve tek günlük göreve bitiş tarihi verilemiyordu.
    if (pStart !== toDateInput(t.plannedStart)) payload.plannedStart = pStart ? localMidnight(pStart) : null;
    if (pEnd !== toDateInput(t.plannedEnd)) payload.plannedEnd = pEnd ? localMidnight(pEnd) : null;
    if (numDirty(estimate, t.estimateHours)) {
      if (estimate.trim() === "") {
        payload.estimateHours = null;
      } else {
        // Geçersiz girdi SESSİZCE null'a çevrilmemeli: "16 saat" yazan kullanıcı
        // kaydın başarılı olduğunu görüyor ama tahmin siliniyor ve üst görevin
        // ilerleme ağırlığı farkında olmadan kayıyordu.
        const n = Number(estimate.replace(",", "."));
        if (!Number.isFinite(n) || n <= 0) { setErr("Tahmini süre pozitif bir sayı olmalı (örn. 16 veya 7,5)."); return; }
        payload.estimateHours = n;
      }
    }
    if (numDirty(spent, t.spentHours)) {
      if (spent.trim() === "") {
        payload.spentHours = null;
      } else {
        // Tahmini süredeki sessiz-null tuzağının aynısı: geçersiz girdi kaydı
        // "başarılı" gösterip değeri uçurmamalı.
        const n = Number(spent.replace(",", "."));
        if (!Number.isFinite(n) || n < 0) { setErr("Harcanan süre negatif olmayan bir sayı olmalı (örn. 6 veya 7,5)."); return; }
        payload.spentHours = n;
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ok = await updateMut.mutateAsync(payload as any).then(() => true).catch(() => false);
    if (!ok) return;
    // Yerel metni sunucunun kanonik biçimine çek ("7,5" → "7.5"). Böylece bir
    // sonraki sunucu tazelemesinde `sync` yeniden hizalanır ve alan sunucudan
    // gelen değişiklikleri tekrar izlemeye başlar.
    if (payload.estimateHours !== undefined) setEstimate(payload.estimateHours == null ? "" : String(payload.estimateHours));
    if (payload.spentHours !== undefined) setSpent(payload.spentHours == null ? "" : String(payload.spentHours));
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !t) return;
    setErr(null);
    const reader = new FileReader();
    reader.onload = () => uploadMut.mutate({ taskId: t.id, fileName: file.name, dataBase64: String(reader.result) });
    reader.readAsDataURL(file);
  }

  function sendComment() {
    if (!t || !body.trim()) return;
    setErr(null);
    commentMut.mutate(
      { taskId: t.id, body: body.trim(), parentId: replyTo?.id ?? null, quotedCommentId: replyTo?.quote ? replyTo.id : null },
      { onSuccess: () => { setBody(""); setReplyTo(null); } },
    );
  }

  // ── Yorum ağacı ───────────────────────────────────────────────────
  type C = NonNullable<typeof commentsQ.data>[number];
  const commentTree = useMemo(() => {
    const rows = commentsQ.data ?? [];
    const kids = new Map<string, C[]>();
    const roots: C[] = [];
    for (const c of rows) {
      if (c.parentId) { const l = kids.get(c.parentId) ?? []; l.push(c); kids.set(c.parentId, l); }
      else roots.push(c);
    }
    return { roots, kids };
  }, [commentsQ.data]);

  function renderComment(c: C, depth: number): React.ReactNode {
    const kids = commentTree.kids.get(c.id) ?? [];
    const isEditing = editing?.id === c.id;
    return (
      <div key={c.id} className="tc-node" style={{ marginLeft: depth === 0 ? 0 : 20 }}>
        <div className={`tc-item ${c.deleted ? "is-del" : ""}`}>
          <div className="tc-head">
            <span className="tc-av">{(c.authorName ?? "?").charAt(0).toUpperCase()}</span>
            <b className="tc-who">{c.authorName ?? "—"}</b>
            <span className="tc-when mono">{fmtDateTime(c.createdAt)}</span>
            {c.edited && !c.deleted ? <span className="tc-tag">düzenlendi</span> : null}
          </div>

          {c.quoted ? (
            <blockquote className="tc-quote">
              <span className="tc-quote-who">{c.quoted.authorName ?? "—"}</span>
              <span>{c.quoted.body ?? "(silinmiş yorum)"}</span>
            </blockquote>
          ) : null}

          {c.deleted ? (
            <p className="tc-body is-del">Bu yorum silindi.</p>
          ) : isEditing ? (
            <div className="tc-edit">
              <textarea className="form-textarea" rows={3} value={editing.body} onChange={(e) => setEditing({ id: c.id, body: e.target.value })} />
              <div className="tc-edit-acts">
                <button type="button" className="btn-md primary" disabled={commentEditMut.isPending || !editing.body.trim()}
                  onClick={() => commentEditMut.mutate({ id: c.id, body: editing.body.trim() }, { onSuccess: () => setEditing(null) })}>Kaydet</button>
                <button type="button" className="btn-md ghost" onClick={() => setEditing(null)}>Vazgeç</button>
              </div>
            </div>
          ) : (
            <p className="tc-body">{c.body}</p>
          )}

          {!c.deleted && !isEditing ? (
            <div className="tc-acts">
              <button type="button" onClick={() => { setReplyTo({ id: c.id, author: c.authorName ?? "—", quote: false }); setBody(""); }}>
                <Icon name="cornerDownRight" size={11} />Yanıtla
              </button>
              <button type="button" onClick={() => { setReplyTo({ id: c.id, author: c.authorName ?? "—", quote: true }); setBody(""); }}>
                <Icon name="fileText" size={11} />Alıntıla
              </button>
              {/* Düzenleme YALNIZ yazara — sunucu da öyle; herkese göstermek
                  garantili FORBIDDEN üretiyordu. */}
              {c.mine ? (
                <button type="button" onClick={() => setEditing({ id: c.id, body: c.body ?? "" })}>
                  <Icon name="edit" size={11} />Düzenle
                </button>
              ) : null}
              {/* Silme: yazar VEYA proje yöneticisi (sunucu da böyle). Herkese
                  göstermek garantili FORBIDDEN üretiyordu. */}
              {c.mine || canManage ? (
                <button type="button" className="danger" onClick={() => setDelComment({ id: c.id, who: c.authorName ?? "—" })}>
                  <Icon name="trash" size={11} />Sil
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        {kids.map((k) => renderComment(k, depth + 1))}
      </div>
    );
  }

  const userOptions = (usersQ.data ?? []).map((u) => ({ value: u.id, label: u.name }));
  const nextStatuses = t ? TASK_TRANSITIONS[t.status as TaskStatus] : [];
  // (Faz 5.6-D) Tamamlanmış/iptal PROJEDE görev SALT-OKUMA: planlama/durum/atama/
  // altgörev/bağımlılık kontrolleri kapatılır (sunucu da reddeder). Yorum + ek AÇIK.
  const projLocked = !!t && (t.project.status === "completed" || t.project.status === "cancelled");
  const canEdit = canManage && !projLocked;

  return (
    <Drawer
      open={open} onClose={onClose} size="xxl"
      title={t ? `${t.code} — ${t.title}` : "Görev"}
      subtitle={t ? `${t.project.name} · WBS ${t.wbs}` : undefined}
      footer={
        <div style={{ display: "flex", gap: 8, width: "100%" }}>
          {t && !projLocked && (t.myScope === "full" || t.myScope === "progressOnly") ? (
            <button type="button" className="btn-md primary" disabled={!dirty || updateMut.isPending} onClick={save}>
              {updateMut.isPending ? "Kaydediliyor…" : "Kaydet"}
            </button>
          ) : null}
          <div style={{ flex: 1 }} />
          <button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>
        </div>
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

      {taskQ.isLoading || !t ? (
        <div style={{ padding: 8, color: "var(--neutral-500)", fontSize: 13 }}>Yükleniyor…</div>
      ) : (
        <div className="td-wrap">
          {projLocked ? (
            <div className="form-banner-err td-locked" role="status">
              <Icon name="alertTriangle" size={14} />
              <span>Proje {t.project.status === "cancelled" ? "iptal/arşiv" : "tamamlanmış"} — görev salt-okunur. Yorum/dosya eklenebilir; düzenlemek için projeyi yeniden açın.</span>
            </div>
          ) : null}
          {/* Durum şeridi */}
          <div className="td-strip">
            <span className={`badge-pill ${STATUS_META.task[t.status as TaskStatus].cls}`}>
              <Icon name={STATUS_META.task[t.status as TaskStatus].icon} size={11} />
              {STATUS_META.task[t.status as TaskStatus].label}
            </span>
            <span className={`badge-pill ${PRIORITY_META[t.priority as keyof typeof PRIORITY_META].cls}`}>
              <Icon name={PRIORITY_META[t.priority as keyof typeof PRIORITY_META].icon} size={11} />
              {PRIORITY_META[t.priority as keyof typeof PRIORITY_META].label}
            </span>
            {t.isMilestone ? <span className="badge-pill b-primary"><Icon name="star" size={11} />Kilometre Taşı</span> : null}
            <div className="grow" />
            {/* Sunucu yetkiyi GÖREV BAZINDA veriyor; `canReport` ise global bir
                bayrak. Global bayrağa güvenmek, kullanıcıya kesinlikle FORBIDDEN
                dönecek butonlar gösteriyordu. `t.myScope` sunucudan gelir. */}
            {!projLocked && (t.myScope === "full" || t.myScope === "progressOnly") && nextStatuses.length ? (
              <div className="td-status-btns">
                {nextStatuses.map((s) => (
                  <button key={s} type="button" className="btn-md secondary" disabled={statusMut.isPending}
                    onClick={() => { setErr(null); statusMut.mutate({ id: t.id, status: s }); }}>
                    <Icon name={STATUS_META.task[s].icon} size={13} />{STATUS_META.task[s].label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="dt2-tabs td-tabs">
            <button type="button" className={`dt2-tab ${tab === "detail" ? "active" : ""}`} onClick={() => setTab("detail")}>Detay</button>
            <button type="button" className={`dt2-tab ${tab === "comments" ? "active" : ""}`} onClick={() => setTab("comments")}>Yorumlar</button>
            <button type="button" className={`dt2-tab ${tab === "activity" ? "active" : ""}`} onClick={() => setTab("activity")}>Hareketler</button>
          </div>

          {tab === "detail" ? (
            <>
              <div className="d-card">
                <div className="d-card-body tight td-form">
                  <div className="form-field td-span">
                    <label className="lbl" htmlFor="td-title">Başlık</label>
                    <input id="td-title" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canEdit} />
                  </div>
                  <div className="form-field td-span">
                    <label className="lbl" htmlFor="td-desc">Görev Tanımı</label>
                    <textarea id="td-desc" className="form-textarea" rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} disabled={!canEdit} />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="td-pri">Öncelik</label>
                    <select id="td-pri" className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)} disabled={!canEdit}>
                      {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="td-prog">İlerleme (%)</label>
                    <input id="td-prog" className="form-input" type="number" min={0} max={100} value={progress}
                      onChange={(e) => {
                        // Boş bırakılabilmeli: `Number("") || 0` anında 0 yazıp
                        // kullanıcının yeni değeri yazmasını zorlaştırıyordu.
                        const raw = e.target.value;
                        if (raw === "") { setProgress(0); return; }
                        const n = Number(raw);
                        if (Number.isFinite(n)) setProgress(Math.max(0, Math.min(100, n)));
                      }} disabled={t?.myScope === "none" || projLocked || hasChildren} />
                    <span className="help">{hasChildren ? "Bu görevin alt görevleri var — ilerleme onlardan hesaplanır." : "Alt görev eklendiğinde bu değer alt görevlerden hesaplanır."}</span>
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="td-ps">Planlanan Başlangıç</label>
                    <input id="td-ps" className="form-input" type="date" value={pStart} onChange={(e) => setPStart(e.target.value)} disabled={!canEdit} />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="td-pe">Planlanan Bitiş</label>
                    <input id="td-pe" className="form-input" type="date" value={pEnd} onChange={(e) => setPEnd(e.target.value)} disabled={!canEdit} />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="td-est">Tahmini Süre (saat)</label>
                    <input id="td-est" className="form-input" value={estimate} onChange={(e) => setEstimate(e.target.value)} disabled={!canEdit} inputMode="decimal" />
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor="td-spent">Harcanan Süre (saat)</label>
                    <input id="td-spent" className="form-input" value={spent} onChange={(e) => setSpent(e.target.value)}
                      disabled={t?.myScope === "none" || projLocked} inputMode="decimal" placeholder="Örn. 6" />
                    <span className="help">Fiilen harcanan efor — tahminle karşılaştırma için.</span>
                  </div>
                  <div className="form-field">
                    <span className="lbl">Gerçekleşen</span>
                    <p className="td-ro mono">{fmtDateTime(t.actualStart)} → {fmtDateTime(t.actualEnd)}</p>
                  </div>
                </div>
              </div>

              {/* Atamalar */}
              <section className="td-sec">
                <div className="td-sec-head">
                  <span className="td-sec-title">Atamalar</span>
                  {canEdit ? (
                    <div className="td-assign-add">
                      <select className="form-select" value={assignRole} onChange={(e) => setAssignRole(e.target.value as TaskRole)} aria-label="Rol">
                        {ROLES.map((r) => <option key={r} value={r}>{STATUS_META.roleLabel[r]}</option>)}
                      </select>
                      <SearchSelect
                        value={null} options={userOptions}
                        onChange={(v) => { if (v && t) { setErr(null); assignMut.mutate({ taskId: t.id, userId: v, role: assignRole, assigned: true }); } }}
                        placeholder="Kişi ekle…" searchPlaceholder="Kişi ara…" emptyText="Kullanıcı yok" icon="user"
                      />
                    </div>
                  ) : null}
                </div>
                {t.assignees.length === 0 ? (
                  <p className="td-empty">Henüz atama yok.</p>
                ) : (
                  <div className="td-people">
                    {ROLES.map((r) => {
                      const list = t.assignees.filter((a) => a.role === r);
                      if (!list.length) return null;
                      return (
                        <div key={r} className="td-people-row">
                          <span className={`td-role-tag r-${r}`}>{STATUS_META.roleLabel[r]}</span>
                          <div className="td-people-chips">
                            {list.map((a) => (
                              <span key={a.userId} className="td-chip">
                                <span className="tc-av sm">{a.name.charAt(0).toUpperCase()}</span>
                                {a.name}
                                {canEdit ? (
                                  <button type="button" aria-label={`${a.name} atamasını kaldır`}
                                    onClick={() => { setErr(null); assignMut.mutate({ taskId: t.id, userId: a.userId, role: r, assigned: false }); }}>
                                    <Icon name="x" size={11} />
                                  </button>
                                ) : null}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Ekler */}
              <section className="td-sec">
                <div className="td-sec-head">
                  <span className="td-sec-title">Dosyalar ({t.attachments.length})</span>
                  <>
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" style={{ display: "none" }} onChange={onPickFile} />
                    <button type="button" className="btn-md secondary" onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending}>
                      <Icon name="upload" size={14} />{uploadMut.isPending ? "Yükleniyor…" : "Foto / PDF Ekle"}
                    </button>
                  </>
                </div>
                {t.attachments.length === 0 ? (
                  <p className="td-empty">Dosya eklenmemiş.</p>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {t.attachments.map((a) => (
                      <div key={a.id} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <DocChip url={a.url} onOpen={setPreviewUrl} onRemove={() => attDelMut.mutate({ id: a.id })} busy={attDelMut.isPending} isPdf={a.kind === "document"} />
                        <span className="td-file-meta">{a.uploadedByName ? `${a.uploadedByName} · ` : ""}{fmtDateTime(a.createdAt)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <DependencyPanel
                taskId={t.id} projectId={t.project.id} canManage={canEdit}
                onError={setErr} onOpenTask={onOpenTask}
              />

              {canEdit ? (
                <button type="button" className="btn-md ghost td-sub-btn" onClick={() => onAddSubtask(t.id)}>
                  <Icon name="cornerDownRight" size={14} />Alt Görev Ekle
                </button>
              ) : null}
            </>
          ) : tab === "comments" ? (
            <section className="td-sec">
              <div className="tc-compose">
                {replyTo ? (
                  <div className="tc-replying">
                    <Icon name={replyTo.quote ? "fileText" : "cornerDownRight"} size={12} />
                    <span><b>{replyTo.author}</b> kullanıcısına {replyTo.quote ? "alıntılı " : ""}yanıt</span>
                    <button type="button" onClick={() => setReplyTo(null)} aria-label="Yanıtı iptal et"><Icon name="x" size={12} /></button>
                  </div>
                ) : null}
                <textarea className="form-textarea" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Yorum yazın…" />
                <div className="tc-compose-acts">
                  <button type="button" className="btn-md primary" disabled={!body.trim() || commentMut.isPending} onClick={sendComment}>
                    <Icon name="check" size={14} />{commentMut.isPending ? "Gönderiliyor…" : "Gönder"}
                  </button>
                </div>
              </div>

              {commentsQ.isLoading ? (
                <p className="td-empty">Yükleniyor…</p>
              ) : commentTree.roots.length === 0 ? (
                <p className="td-empty">Henüz yorum yok — ilk yorumu siz yazın.</p>
              ) : (
                <div className="tc-list">{commentTree.roots.map((c) => renderComment(c, 0))}</div>
              )}
            </section>
          ) : (
            <section className="td-sec">
              {eventsQ.isLoading ? (
                <p className="td-empty">Yükleniyor…</p>
              ) : (eventsQ.data ?? []).length === 0 ? (
                <p className="td-empty">Hareket yok.</p>
              ) : (
                <ul className="td-feed">
                  {(eventsQ.data ?? []).map((ev) => (
                    <li key={ev.id}>
                      <span className="td-feed-ic" aria-hidden="true"><Icon name={eventIcon(ev)} size={13} /></span>
                      <span className="td-feed-txt">
                        <span className="td-feed-who">{ev.actorName ?? "Sistem"}</span> {eventText(ev)}
                      </span>
                      <span className="td-feed-when mono">{fmtDateTime(ev.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* Ata GÖRÜNÜR değilse link çizilmez: görünürlük alt ağaca miras kalır,
              yukarı doğru DEĞİL — tıklayan "Görev bulunamadı" duvarına çarpıyordu. */}
          {t.parentId && t.parentVisible ? (
            <button type="button" className="btn-md ghost td-parent" onClick={() => onOpenTask(t.parentId!)}>
              <Icon name="chevLeft" size={14} />Üst göreve git
            </button>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        open={!!delComment}
        title="Yorum silinsin mi?"
        message={<><b>{delComment?.who}</b> tarafından yazılan yorum silinecek. Yorum listede kalır, içeriği gizlenir; altındaki cevaplar korunur.</>}
        tone="danger" confirmLabel="Sil" cancelLabel="Vazgeç"
        busy={commentDelMut.isPending}
        onConfirm={() => delComment && commentDelMut.mutate({ id: delComment.id }, { onSuccess: () => setDelComment(null) })}
        onCancel={() => setDelComment(null)}
      />
      <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
    </Drawer>
  );
}
